<template>
  <v-container fluid class="settings-page pa-6">
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-1">الإعدادات</h1>
      <p class="text-medium-emphasis">تخصيص إعدادات النظام والتفضيلات العامة</p>
    </div>

    <v-row>
      <!-- Menu -->
      <v-col cols="12" md="4">
        <v-card class="rounded-xl elevation-1">
          <v-list nav density="comfortable">
            <v-list-item
              v-for="item in menuItems"
              :key="item.value"
              :active="activeTab === item.value"
              :prepend-icon="item.icon"
              :title="item.title"
              rounded="lg"
              @click="activeTab = item.value"
            />
          </v-list>
        </v-card>
      </v-col>

      <!-- Content -->
      <v-col cols="12" md="8">
        <!-- General -->
        <v-card v-if="activeTab === 'general'" class="rounded-xl elevation-1">
          <v-card-title class="font-weight-bold">الإعدادات العامة</v-card-title>
          <v-divider />

          <v-card-text>
            <v-form>
              <v-text-field
                v-model="settings.systemName"
                label="اسم النظام"
                variant="outlined"
                prepend-inner-icon="mdi-cog"
              />

              <v-select
                v-model="settings.language"
                label="لغة النظام"
                :items="languages"
                variant="outlined"
                prepend-inner-icon="mdi-translate"
              />

              <v-select
                v-model="settings.theme"
                label="المظهر"
                :items="themes"
                variant="outlined"
                prepend-inner-icon="mdi-theme-light-dark"
              />

              <v-switch
                v-model="settings.notifications"
                label="تفعيل الإشعارات"
                color="primary"
                inset
              />

              <v-switch
                v-model="settings.autoBackup"
                label="تفعيل النسخ الاحتياطي التلقائي"
                color="primary"
                inset
              />

              <v-divider class="my-4" />

              <div class="d-flex justify-end gap-2">
                <v-btn variant="outlined" @click="resetSettings">إلغاء</v-btn>
                <v-btn color="primary" @click="saveSettings"
                  >حفظ الإعدادات</v-btn
                >
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Users -->
        <v-card v-if="activeTab === 'users'" class="rounded-xl elevation-1">
          <v-card-title class="font-weight-bold"
            >المستخدمون والصلاحيات</v-card-title
          >
          <v-divider />

          <v-card-text>
            <v-table>
              <thead>
                <tr>
                  <th>المستخدم</th>
                  <th>الدور</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in users" :key="user.id">
                  <td>{{ user.name }}</td>
                  <td>{{ user.role }}</td>
                  <td>
                    <v-chip
                      :color="user.active ? 'success' : 'error'"
                      size="small"
                    >
                      {{ user.active ? "نشط" : "غير نشط" }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Security -->
        <v-card v-if="activeTab === 'security'" class="rounded-xl elevation-1">
          <v-card-title class="font-weight-bold">الأمان</v-card-title>
          <v-divider />

          <v-card-text>
            <v-switch
              v-model="security.twoFactor"
              label="تفعيل التحقق الثنائي"
              color="primary"
              inset
            />

            <v-switch
              v-model="security.sessionTimeout"
              label="تسجيل الخروج التلقائي عند الخمول"
              color="primary"
              inset
            />

            <v-text-field
              v-model="security.timeoutMinutes"
              label="مدة الخمول بالدقائق"
              type="number"
              variant="outlined"
              prepend-inner-icon="mdi-timer"
            />
          </v-card-text>
        </v-card>

        <!-- Notifications -->
        <v-card
          v-if="activeTab === 'notifications'"
          class="rounded-xl elevation-1"
        >
          <v-card-title class="font-weight-bold">الإشعارات</v-card-title>
          <v-divider />

          <v-card-text>
            <v-switch
              v-model="notificationSettings.email"
              label="إشعارات البريد الإلكتروني"
              color="primary"
              inset
            />

            <v-switch
              v-model="notificationSettings.system"
              label="إشعارات داخل النظام"
              color="primary"
              inset
            />

            <v-switch
              v-model="notificationSettings.reports"
              label="إشعارات التقارير الجديدة"
              color="primary"
              inset
            />
          </v-card-text>
        </v-card>

        <!-- System Info -->
        <v-card class="rounded-xl elevation-1 mt-6">
          <v-card-title class="font-weight-bold">معلومات النظام</v-card-title>
          <v-divider />

          <v-card-text>
            <v-list density="compact">
              <v-list-item
                title="الإصدار"
                subtitle="HFAS v1.0.0"
                prepend-icon="mdi-information"
              />

              <v-list-item
                title="آخر تحديث"
                subtitle="2026-06-11"
                prepend-icon="mdi-update"
              />

              <v-list-item
                title="حالة النظام"
                subtitle="يعمل بشكل طبيعي"
                prepend-icon="mdi-check-circle"
              />
            </v-list>
          </v-card-text>
        </v-card>

        <v-snackbar v-model="snackbar.show" color="success">
          {{ snackbar.text }}
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from "vue";

const activeTab = ref("general");

const menuItems = [
  {
    title: "الإعدادات العامة",
    value: "general",
    icon: "mdi-cog-outline",
  },
  {
    title: "المستخدمون والصلاحيات",
    value: "users",
    icon: "mdi-account-group-outline",
  },
  {
    title: "الأمان",
    value: "security",
    icon: "mdi-shield-lock-outline",
  },
  {
    title: "الإشعارات",
    value: "notifications",
    icon: "mdi-bell-outline",
  },
];

const languages = ["العربية", "English"];
const themes = ["فاتح", "داكن", "حسب النظام"];

const defaultSettings = {
  systemName: "نظام تقييم المرافق الصحية HFAS",
  language: "العربية",
  theme: "فاتح",
  notifications: true,
  autoBackup: false,
};

const settings = ref({ ...defaultSettings });

const security = ref({
  twoFactor: false,
  sessionTimeout: true,
  timeoutMinutes: 30,
});

const notificationSettings = ref({
  email: true,
  system: true,
  reports: true,
});

const users = ref([
  {
    id: 1,
    name: "Basel Ahmed",
    role: "مدير النظام",
    active: true,
  },
  {
    id: 2,
    name: "Data Collector",
    role: "باحث ميداني",
    active: true,
  },
  {
    id: 3,
    name: "Viewer",
    role: "مشاهد تقارير",
    active: false,
  },
]);

const snackbar = ref({
  show: false,
  text: "",
});

const saveSettings = () => {
  localStorage.setItem("hfas_settings", JSON.stringify(settings.value));
  snackbar.value = {
    show: true,
    text: "تم حفظ الإعدادات بنجاح",
  };
};

const resetSettings = () => {
  settings.value = { ...defaultSettings };
};
</script>

<style scoped>
.settings-page {
  direction: rtl;
}

.gap-2 {
  gap: 8px;
}

:deep(.v-list-item--active) {
  background: rgba(74, 79, 85, 0.12);
  color: #4a4f55;
  font-weight: 700;
}
</style>
